import { css } from '@emotion/react';
import { useMemo } from 'react';
import { AutocompleteComponent } from '~/_shared/baseComponents/autocomplete';
import { ButtonComponent } from '~/_shared/baseComponents/buttons/button/button.component';
import { ButtonStyle } from '~/_shared/baseComponents/buttons/button/button.types';
import {
  type DropdownOption, RegularDropdownComponent,
} from '~/_shared/baseComponents/dropdown';
import { SwitchOptionsComponent } from '~/_shared/components/switchOptions/switchOptions.component';
import { useTranslation } from '~/_shared/utils/hooks';
import { ContinueButton } from '~/map/layered/createLayeredMapModal/ContinueButton';
import {
  BuildingBlockSelections,
  BuildingBlockSubsetTypes,
} from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/createBoundaryGroupFromAIModal.types';
import { type BoundaryId } from '~/store/boundaries/boundaryIdentifier.type';

// From data option si currently disabled thus the entire selector is not shown
const SUBSET_TYPE_SELECTION_ENABLED = false;

const rowGap = 20;
const gridContainerStyle = ({ showSubset }: { showSubset: boolean }) => css({
  display: 'grid',
  gridTemplateColumns: '1fr auto',
  gridTemplateAreas: `
    'blockTypeLabel blockTypeLabel'
    'blockType subset'
    ${showSubset ? `
      'subsetLabel subsetLabel'
      ${SUBSET_TYPE_SELECTION_ENABLED ? '\'subsetSelect goToMap\'' : '\'goToMap goToMap\''}
    ` : ''}
    'selectedBlocks continue'
  `,
  alignItems: 'center',
  justifyItems: 'start',
  gap: `${rowGap}px 15px`,
});

const labelStyle = css({
  marginBottom: 5 - rowGap,
});

export type BuildingBlockStepComponentProps<T> = Readonly<{
  value: T | null;
  options: ReadonlyArray<DropdownOption<T>>;
  onChange: (value: T) => void;
  onContinue: () => void;
  buildingBlockSelection: BuildingBlockSelections;
  buildingBlockSubsetType: string;
  selectedBoundaryIds?: ReadonlyArray<BoundaryId>;
  onSelectSubsetFromMap: () => void;
  onChangeBuildingBlockSelection: (value: BuildingBlockSelections) => void;
  onChangeBuildingBlockSubsetType: (value: string) => void;
}>;

export const BuildingBlockStepComponent = <T, >({
  value,
  options,
  onChange,
  onContinue,
  onSelectSubsetFromMap,
  buildingBlockSelection,
  buildingBlockSubsetType,
  selectedBoundaryIds,
  onChangeBuildingBlockSelection,
  onChangeBuildingBlockSubsetType,
}: BuildingBlockStepComponentProps<T>) => {
  const [t] = useTranslation();

  const subsetOptions = useMemo<{
    name: string;
    value: string;
  }[]>(() => ([
    {
      name: t('boundaryAI.On Map'),
      value: BuildingBlockSubsetTypes.ON_MAP_BY_LASSO,
    },
    {
      name: t('boundaryAI.From Data'),
      value: BuildingBlockSubsetTypes.FROM_DATA,
    },
  ]), [t]);

  const isManualSelectionRequired = useMemo(() =>
    !value || (
      buildingBlockSelection === BuildingBlockSelections.SUBSET &&
      buildingBlockSubsetType !== BuildingBlockSubsetTypes.FROM_DATA &&
      !selectedBoundaryIds?.length
    ), [value, buildingBlockSubsetType, buildingBlockSelection, selectedBoundaryIds],
  );

  const isSubmitDisabled = !value || isManualSelectionRequired;
  const showSubset = buildingBlockSelection === BuildingBlockSelections.SUBSET;
  const blockTypeLabel = options.find(o => o.value === value)?.name;

  return (
    <div css={gridContainerStyle({ showSubset })}>
      <span css={[{ gridArea: 'blockTypeLabel' }, labelStyle]}>{t('boundaryAI.Building Block Type')}</span>
      <AutocompleteComponent
        css={[{ gridArea: 'blockType', justifySelf: 'stretch' }]}
        value={value}
        onChange={onChange}
        multiple={false}
        options={options}
        inPortal
      />
      <SwitchOptionsComponent
        css={{ gridArea: 'subset', justifySelf: 'end' }}
        isDisabled={!value}
        options={[{
          value: BuildingBlockSelections.ALL,
          label: t('all').toUpperCase(),
          tooltip: blockTypeLabel && t('boundaryAI.Include all {{blocks}} for the territory generation',
            { blocks: blockTypeLabel }),
        }, {
          value: BuildingBlockSelections.SUBSET,
          label: t('boundaryAI.A subset of').toUpperCase(),
          tooltip: blockTypeLabel && t('boundaryAI.Include only a subset of {{blocks}} for the territory generation',
            { blocks: blockTypeLabel }),
        }]}
        value={buildingBlockSelection}
        onChange={onChangeBuildingBlockSelection}
      />
      {showSubset && (
        <>
          <span css={[{ gridArea: 'subsetLabel' }, labelStyle]}>
            {SUBSET_TYPE_SELECTION_ENABLED ?
              t('boundaryAI.Choose Subset Selection Method') :
              t('boundaryAI.Select the subset area on map')}
          </span>
          {SUBSET_TYPE_SELECTION_ENABLED && (
            <div>
              <RegularDropdownComponent
                css={{ gridArea: 'subsetSelect', justifySelf: 'stretch' }}
                onChange={onChangeBuildingBlockSubsetType}
                value={buildingBlockSubsetType}
                options={subsetOptions}
                inPortal
              />
            </div>
          )}
          {
            buildingBlockSubsetType !== BuildingBlockSubsetTypes.FROM_DATA && (
              <ButtonComponent
                css={{
                  gridArea: 'goToMap',
                  width: !SUBSET_TYPE_SELECTION_ENABLED ? 140 : undefined,
                  justifySelf: SUBSET_TYPE_SELECTION_ENABLED ? 'stretch' : undefined,
                }}
                buttonStyle={ButtonStyle.Secondary}
                text={selectedBoundaryIds?.length ? t('Edit Selection') : t('Go To Map')}
                onClick={onSelectSubsetFromMap}
              />
            )}
        </>
      )}
      {selectedBoundaryIds?.length ? (
        <span css={{ gridArea: 'selectedBlocks' }}>
          {t('boundaryAI.{{count}} blocks selected', { count: selectedBoundaryIds.length })}
        </span>
      ) : null}
      <ContinueButton
        css={{ gridArea: 'continue', justifySelf: 'end' }}
        label={t('Continue')}
        onSubmitClick={onContinue}
        disabled={isSubmitDisabled}
      />
    </div>
  );
};
